// An aggregation based on terms on Elastic Search has a default size value of 10,
// meaning that it will only return the top 10 most frequent terms that it will
// find (ex : the 10 most frequent provider_id). But, we don't want to exclude
// provider_id or any sort of terms because they don't make it to the top ten
// and we want our data to be precise and accurate ( **see explanation below).
// Therefore we should always give a size above 10 (or the right number of
// terms that we want to fetch) for any aggregation based on terms that we
// need to do, to avoid missing unfrequent terms.
// Therefore we chose to put a size to 100 if there was no other indication
// in order to change the default value of 10.

import { IconType } from 'react-icons'
import { BiCar } from 'react-icons/bi'
import { GrBike } from 'react-icons/gr'
import { MdCommute } from 'react-icons/md'
import { RiEBike2Line } from 'react-icons/ri'
import { TbScooter } from 'react-icons/tb'

import {
  AgencySettings_VehicleStatesEnum,
  AgencySettings_VehicleTypesEnum,
  Offense_StatusEnum,
  Policy_KindEnum,
} from 'src/api/gen'

import CargoBikeIcon from './icons/CargoBikeIcon'
import DeliveryBotIcon from './icons/DeliveryBotIcon'

// ** accuracy problem may appear as each shard only return a part of
// the total count of each occurences.
// See for more detail :
// https://www.elastic.co/guide/en/elasticsearch/reference/current/search-aggregations-bucket-terms-aggregation.html

// We chose the value of 100 because we think that's a goo balance between
// precision and computation-time :
// "The higher the requested size is, the more accurate the results will be,
// but also, the more expensive it will be to compute the final results
// (both due to bigger priority queues that are managed on a shard level
// and due to bigger data transfers between the nodes and the client)." (link above)

// The good practice would be to put the exact number of different terms for each aggregation.

export const DEFAULT_AGGREGATION_SIZE = 100
export const FEE_RATES_POLICY_KINDS = [
  Policy_KindEnum.trip_taxation,
  Policy_KindEnum.permit,
  Policy_KindEnum.vehicle_deployment,
] as const

export const VEHICLES_CATEGORY_ICON_MAP: Record<string, IconType> = {
  [AgencySettings_VehicleTypesEnum.bicycle]: GrBike,
  [AgencySettings_VehicleTypesEnum.car]: BiCar,
  [AgencySettings_VehicleTypesEnum.cargo_bicycle]: CargoBikeIcon,
  [AgencySettings_VehicleTypesEnum.delivery_bot]: DeliveryBotIcon,
  [AgencySettings_VehicleTypesEnum.moped]: RiEBike2Line,
  [AgencySettings_VehicleTypesEnum.other]: MdCommute,
  [AgencySettings_VehicleTypesEnum.scooter]: TbScooter,
}

/**
 *
 * @param category
 * @returns
 */
export function getVehicleCategoryIcon(category: string): IconType {
  return VEHICLES_CATEGORY_ICON_MAP[category]
}

interface IColorItem {
  background: string
  text: string
}

export const VEHICLES_STATUS_COLOR_MAP: Record<string, IColorItem> = {
  [AgencySettings_VehicleStatesEnum.available]: { background: '#DDFFDD', text: '#5CB85C' },
  [AgencySettings_VehicleStatesEnum.elsewhere]: { background: '#DDFFDD', text: '#5CB85C' },
  [AgencySettings_VehicleStatesEnum.non_operational]: { background: '#FFF2E3', text: '#FFB259' },
  [AgencySettings_VehicleStatesEnum.on_trip]: { background: '#E1F0FF', text: '#0171E4' },
  [AgencySettings_VehicleStatesEnum.removed]: { background: '#ffadab', text: '#7c002e' },
  [AgencySettings_VehicleStatesEnum.reserved]: { background: '#E1F0FF', text: '#0171E4' },
  [AgencySettings_VehicleStatesEnum.unknown]: { background: '#BDBDBD', text: '#616161' },
  [AgencySettings_VehicleStatesEnum.stopped]: { background: '#faf7cf', text: '#f5d742' },
}

/**
 *
 * @param vehicleStatus
 * @returns
 */
export function getVehicleStatusColor(vehicleStatus: AgencySettings_VehicleStatesEnum): IColorItem {
  return VEHICLES_STATUS_COLOR_MAP[vehicleStatus]
}

export const OFFENSE_STATUS_COLOR_MAP: Record<string, IColorItem> = {
  [Offense_StatusEnum.agent_to_check]: { background: '#FFF2E3', text: '#FFB259' },
  [Offense_StatusEnum.auto_confirmed]: { background: '#FFE3E3', text: '#DE3D4C' },
  [Offense_StatusEnum.confirmed_by_agent]: { background: '#FFE3E3', text: '#DE3D4C' },
  [Offense_StatusEnum.invalidated]: { background: '#DDFFDD', text: '#5CB85C' },
}

/**
 *
 * @param offenseStatus
 * @returns
 */
export function getOffenseStatusColor(offenseStatus?: Offense_StatusEnum): IColorItem {
  if (!offenseStatus) {
    return OFFENSE_STATUS_COLOR_MAP[Offense_StatusEnum.auto_confirmed]
  }

  return OFFENSE_STATUS_COLOR_MAP[offenseStatus]
}

export const DEFAULT_HOMEPAGE_URL = '/'
